import { Injectable } from '@angular/core';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { ApiResponse, ExtendedProfile } from '../../../../types/types';
import { ClientService } from './client/client.service';
import { Observable, of } from 'rxjs';

export interface UserTokenPayload {
  roles?: string[];
  displayName: string;
  email: string;
  givenName: string;
  netid: string;
  sn: string;
  uid: string;
}

@Injectable()
export class AuthenticationService extends ClientService {
  private userTokenPayload: UserTokenPayload | null = null;
  private userTokenObservable$?: Observable<UserTokenPayload>;

  // constructor(private http: HttpClient) {}

  // rewrite this to handle <ApiResponse<UserTokenPayload>> extract the payload from the response.data
  public getAuthenticatedUser(): Observable<UserTokenPayload | undefined> {
    if (!this.isBrowserOnly) {
      return of({
        roles: [''],
        displayName: '',
        email: '',
        givenName: '',
        netid: '',
        sn: '',
        uid: '',
      });
    }
    if (!this.userTokenObservable$) {
      this.userTokenObservable$ = this.http
        .get<ApiResponse<UserTokenPayload>>(`${this.baseUrl}/api/admin`) // Adjusted to expect ApiResponse<UserTokenPayload>
        .pipe(
          map((response) => {
            // Check if the response is valid and has a data property
            if (response && response.data) {
              return response.data; // Extract the payload from response.data
            }
            throw new Error('Invalid response structure');
          }),
          catchError(
            AuthenticationService.handleError<UserTokenPayload>('login')
          ),
          shareReplay(1)
        );
    }
    return this.userTokenObservable$;
  }

  checkProfile(): Observable<ApiResponse<ExtendedProfile>> {
    if (this.isBrowserOnly) {
      return this.get<ApiResponse<ExtendedProfile>>(
        `${this.baseUrl}/api/profile`,
        undefined,
        {
          storageType: 'memory',
          ttlMS: 3600000, // 1 hour
        }
      );
    }
    return of({ status: 401, message: 'Unauthorized', data: null });
  }

  logout() {
    location.href = '/api/logout';
  }

  // used by MultiRoleGuard look into using observables instead of promises.
  public async getRoles(): Promise<string[] | undefined> {
    const tokenPayload = await this.getTokenPayload();
    if (tokenPayload) {
      return tokenPayload.roles;
    }
    return [];
  }

  // @todo use observables not promises @todo use cache client.
  private getTokenPayload(): Promise<UserTokenPayload> {
    return new Promise((resolve, reject) => {
      if (this.userTokenPayload) {
        return resolve(this.userTokenPayload);
      }
      this.getAuthenticatedUser().subscribe((tokenPayload) => {
        if (tokenPayload) {
          this.userTokenPayload = tokenPayload;
          resolve(tokenPayload);
        } else {
          reject(null);
        }
      });
    });
  }

  private static handleError<T>(operation = 'operation', result?: T) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (error: any): Observable<T> => {
      // console.error(error);
      console.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
